import React, { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { isEqual } from 'lodash';
import Form from '../../form/Form';
import { useFormListContext } from './contexts/FormListContext';
import { formFieldUtils } from './utils';
import { TabFormFields } from './FormListTab';

interface FormListTabPaneProps<FieldsValue> {
  id: number;
  value: FieldsValue;
  onChange: (value: FieldsValue) => void;
  content: (form: WrappedFormUtils) => JSX.Element;
  form: WrappedFormUtils;
}

const FormListTabPaneInner = Form.create<FormListTabPaneProps<any>>({
  name: 'global_state',
  // 각 필드 변경시 필드에 대한 메타데이터가 인자로 들어옵니다.
  // {dirty: true,errors: undefined,name: "username",touched: true,validating: true,value: "aaas"}
  onFieldsChange(props, changedFields, allChangedFields) {
    props.onChange({ ...allChangedFields, id: props.id });
  },
  //각 탭에 속한 폼의 모든 필드의 메타데이터가 value로 들어오고 이를 각 필드에 연결합니다.
  mapPropsToFields(props) {
    return props.value ? props.value : {};
  },
})((props: FormListTabPaneProps<Record<string, unknown>>) => {
  const { id, form, content } = props;
  const formListContext = useFormListContext();

  useEffect(() => {
    if (formListContext) formListContext.setFormList((formList) => [...formList, { id, form }]);
  }, []);

  return <Form form={form}>{content(form)}</Form>;
});

const FormListTabPane = <FieldsValue extends { id: number }>(
  props: Omit<FormListTabPaneProps<FieldsValue>, 'form'>,
): JSX.Element => {
  const { id, value, onChange, content } = props;
  const [field, setField] = useState<TabFormFields<FieldsValue>>();

  /**
   * 만약 각 탭에 name과 price입력하는 Form이 있을 경우
   * value로 [{id:1,name:'name1',price:1000},{id:2,name:'name2',price:1200}] 이 들어오게 됩니다
   * (id는 데이터 불러올때 uuid를 통해 임의 부여 해야합니다)
   * value를 antd의 Form.createFormField를 사용하여 필드 객체로 변환하여 field 상태에 별도로 저장합니다
   *
   * */
  useEffect(() => {
    if (!isEqual(value, field && formFieldUtils.changeFieldToValue(field))) {
      const newField = formFieldUtils.changeValueToField(value);
      setField(newField);
    }
  }, [value]);

  /**
   * 값입력시 각필드의 메타정보를 빼고 값만 onChange로 넘겨줍니다(최종 Form에는 값만 저장되어야 하기 때문입니다)
   * [{id:1,
   * name:{dirty: true,errors: undefined,name: "username",touched: true,validating: true,value: "이름"},
   * price:{dirty: true,errors: undefined,name: "username",touched: true,validating: true,value: 1700},
   * }]
   * -> [{id:1,name:"이름",price:1700}]
   * */
  const handleChange = (field: TabFormFields<FieldsValue>): void => {
    setField(field);
    onChange(formFieldUtils.changeFieldToValue(field));
  };

  return <FormListTabPaneInner id={id} value={field} onChange={handleChange} content={content} />;
};

export default FormListTabPane;
